import { OrganizationDashboardPreview } from './OrganizationDashboardPreview'
import { CourseFormPage } from '@/pages/CourseFormPage'
import { CourseDetailPage } from '@/pages/CourseDetailPage'
/* eslint-disable max-lines */
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppShell } from '@/components/templates/app-shell'
import { StaffBrandHeader } from '@/components/StaffBrandHeader'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { deleteJSON, getJSON, onAuthenticationRequired, postJSON } from '@/lib/api'
import { apiMessage } from '@/lib/contacts/errors'
import type { Contact } from '@/lib/contacts/types'
import { clearExpiredDemoSignIn, hasExpiredDemoSignIn } from '@/lib/demoSignInExpiry'
import { sessionQueryOptions } from '@/lib/sessionQueryOptions'
import { ContactFormPage } from '@/pages/ContactFormPage'
import { ChaptersListPage } from '@/pages/ChaptersListPage'
import { ChapterRecordPage } from '@/pages/ChapterRecordPage'
import { ContactImportWizardPage } from '@/pages/ContactImportWizardPage'
import { ContactsListPage } from '@/pages/ContactsListPage'
import { DuplicateReviewPage } from '@/pages/DuplicateReviewPage'
import { EventsPage } from '@/pages/EventsPage'
import { EventRegistrationsPage } from '@/pages/EventRegistrationsPage'
import { CoursesListPage } from '@/pages/CoursesListPage'
import { EnrolmentImportWizardPage } from '@/pages/EnrolmentImportWizardPage'
import type { Invoice } from '@/pages/InvoicesPage'
import { incomeDrillDownPath, type IncomeInvoiceDrillDown } from '@/lib/incomeInvoiceDrillDown'
import { agingInvoiceDrillDownPath } from '@/lib/agingInvoiceDrillDown'
import type { AgingContactDrillDown } from '@/pages/AgingReportPage'
import { isMembershipView, StaffMembershipContent } from '@/pages/StaffMembershipContent'
import { OrganizationProfilePage } from '@/pages/OrganizationProfilePage'
import { isSettingsView, StaffSettingsView } from '@/pages/StaffSettingsView'
import { isFinanceView, StaffFinanceView } from '@/pages/StaffFinanceView'
import { PersonProfilePage } from '@/pages/PersonProfilePage'
import { isCommunicationView, StaffCommunicationView } from '@/pages/StaffCommunicationView'
import type { ContactsView } from '@/pages/StaffContactsView.types'
import { isEventsView } from '@/pages/StaffEventsView'
import { useStaffView } from '@/pages/useStaffView'
import { staffNav } from '@/pages/staffNavigation'
import { staffAreaForView } from '@/pages/staffAreaForView'
import { NoAccessPage } from '@/pages/NoAccessPage'
import {
  canManage,
  isViewOnly,
  isViewOnlyRole,
  type StaffIdentity,
} from '@/pages/staffCapabilities'
import { moduleForArea } from '@/pages/staffModuleForArea'
import { StaffIdentityError, StaffSessionLoading } from '@/pages/staffSessionStates'
import { clearConnectionReturn } from '@/pages/connections/connectionNavigation'

export interface Association {
  id: number
  name: string
  web_address_name: string
  custom_web_address?: string | null
  time_zone: string
  currency?: string
  current_date?: string
  status?: string
  web_address?: string
  date_format?: 'dmy' | 'mdy'
}

interface SeatResponse {
  seat: {
    user: { email: string }
    association: Association
  }
}

interface SignInForm {
  email: string
  password: string
}

export default function StaffSignInPage({ association }: { association: Association }) {
  const queryClient = useQueryClient()
  const [form, setForm] = React.useState<SignInForm>({ email: '', password: '' })
  const [expiredDemoLink, setExpiredDemoLink] = React.useState(hasExpiredDemoSignIn)
  const [message, setMessage] = React.useState<string | null>(null)
  const seatQuery = useQuery<SeatResponse | null>({
    queryKey: ['seat'],
    queryFn: () => getJSON<SeatResponse>('/api/v1/seat'),
    retry: false,
    ...sessionQueryOptions,
  })
  const identityQuery = useQuery<StaffIdentity>({
    queryKey: ['staff-identity', seatQuery.data?.seat?.association.id],
    queryFn: () => getJSON<StaffIdentity>('/api/v1/me'),
    enabled: Boolean(seatQuery.data?.seat),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  React.useEffect(
    () =>
      onAuthenticationRequired(() => {
        setMessage(null)
        queryClient.setQueryData<SeatResponse | null>(['seat'], null)
      }),
    [queryClient],
  )
  const signIn = useMutation({
    mutationFn: () => postJSON('/api/v1/sessions', form),
    onSuccess: async () => {
      setExpiredDemoLink(false)
      clearExpiredDemoSignIn()
      setMessage(null)
      await queryClient.invalidateQueries({ queryKey: ['seat'] })
    },
    onError: (error: Error) => {
      setMessage(apiMessage(error))
    },
  })
  const signOut = useMutation({
    mutationFn: () => deleteJSON('/api/v1/sessions'),
    onSuccess: async () => {
      setMessage(null)
      queryClient.setQueryData<SeatResponse | null>(['seat'], null)
      await queryClient.invalidateQueries({ queryKey: ['seat'] })
    },
    onError: (error: Error) => {
      setMessage(apiMessage(error))
    },
  })

  if (seatQuery.data?.seat && !expiredDemoLink) {
    return (
      <AuthenticatedStaffPage
        seat={seatQuery.data.seat}
        identity={identityQuery.data}
        identityLoading={identityQuery.isLoading}
        identityError={identityQuery.isError}
        message={message}
        signingOut={signOut.isPending}
        onSignOut={() => signOut.mutate()}
      />
    )
  }

  if (seatQuery.isLoading) {
    return <StaffSessionLoading />
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <StaffBrandHeader brand={association.name} />

        <h1 className="text-heading-sm font-semibold text-foreground">
          Sign in to your association
        </h1>

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            signIn.mutate()
          }}
        >
          {expiredDemoLink && <Alert>Link expired</Alert>}
          {message && <Alert>{message}</Alert>}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="staff-email">Email</Label>
            <Input
              id="staff-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              autoComplete="email"
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="staff-password">Password</Label>
            <Input
              id="staff-password"
              type="password"
              placeholder="password"
              value={form.password}
              autoComplete="current-password"
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
          </div>
          <Button type="submit" className="mt-1 w-full" disabled={signIn.isPending}>
            {signIn.isPending ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  )
}

// function StaffSessionLoading (shared implementation in staffSessionStates.tsx)
function AuthenticatedStaffPage({
  seat,
  identity,
  identityLoading,
  identityError,
  message,
  signingOut,
  onSignOut,
}: {
  seat: SeatResponse['seat']
  identity?: StaffIdentity
  identityLoading: boolean
  identityError: boolean
  message: string | null
  signingOut: boolean
  onSignOut: () => void
}) {
  if (identityLoading) return <StaffSessionLoading />
  if (identityError) return <StaffIdentityError brand={seat.association.name} />
  const capabilities =
    identity?.user.seats.find((candidate) => candidate.association_id === seat.association.id)
      ?.capabilities ?? {}
  return (
    <StaffContactsShell
      association={seat.association}
      userEmail={seat.user.email}
      capabilities={capabilities}
      message={message}
      signingOut={signingOut}
      onSignOut={onSignOut}
    />
  )
}

export function StaffContactsShell({
  association,
  userEmail,
  capabilities,
  message,
  signingOut,
  onSignOut,
}: {
  association: Association
  userEmail: string
  capabilities: StaffIdentity['user']['seats'][number]['capabilities']
  message: string | null
  signingOut: boolean
  onSignOut: () => void
}) {
  const { view, setView, navigateTo } = useStaffView()
  React.useEffect(() => {
    if (!canManage(capabilities, 'connections')) clearConnectionReturn()
  }, [capabilities])

  const showList = () => navigateTo({ kind: 'list' }, '/contacts')
  const showMembers = () => navigateTo({ kind: 'members' }, '/members')
  const showInvoices = () => navigateTo({ kind: 'finance-invoices' }, '/finance/invoices')
  const openInvoice = (invoice: Invoice) =>
    navigateTo({ kind: 'invoice-detail', invoiceId: invoice.id }, `/finance/invoices/${invoice.id}`)
  const openInvoiceById = (invoiceId: number) =>
    navigateTo({ kind: 'invoice-detail', invoiceId }, `/finance/invoices/${invoiceId}`)
  const openIncomeDrillDown = (drillDown: IncomeInvoiceDrillDown) =>
    navigateTo({ kind: 'finance-invoices', drillDown }, incomeDrillDownPath(drillDown))
  const openAgingDrillDown = (agingContact: AgingContactDrillDown) =>
    navigateTo({ kind: 'finance-invoices', agingContact }, agingInvoiceDrillDownPath(agingContact))
  const openCampaign = (campaignId: number) =>
    navigateTo({ kind: 'finance-campaign-detail', campaignId }, `/finance/donations/${campaignId}`)
  const editContact = (contactId: number, contactType: Contact['contact_type']) =>
    setView({ kind: 'edit', contactId, contactType })
  const activeArea = staffAreaForView(view)
  const activeModule = moduleForArea(activeArea)
  const access = capabilities[activeModule] ?? { view: false, manage: false }
  const profileReturn = personProfileReturn(view, showMembers, showList)
  return (
    <AppShell
      associationName={association.name}
      nav={staffNav(activeArea, navigateTo, capabilities)}
      viewOnly={isViewOnly(capabilities, activeModule)}
      showViewOnlyBadge={isViewOnlyRole(capabilities)}
      user={{ name: userEmail, onSignOut, signingOut }}
    >
      {/* Feature 29 permits every authenticated Staff Role to read this setting. */}
      {access.view || view.kind === 'organization-dashboard-setting' ? (
        <StaffPageContent
          view={view}
          setView={setView}
          navigateTo={navigateTo}
          association={association}
          message={message}
          showList={showList}
          showMembers={showMembers}
          showInvoices={showInvoices}
          openInvoice={openInvoice}
          openInvoiceById={openInvoiceById}
          openIncomeDrillDown={openIncomeDrillDown}
          openAgingDrillDown={openAgingDrillDown}
          onOpenCampaign={openCampaign}
          editContact={editContact}
          profileReturn={profileReturn}
          viewOnly={isViewOnly(capabilities, activeModule)}
          viewerEmail={userEmail}
          capabilities={capabilities}
        />
      ) : (
        <NoAccessPage />
      )}
    </AppShell>
  )
}

function StaffPageContent(props: StaffPageContentProps) {
  if (props.view.kind === 'enrolment-import')
    return props.viewOnly ? <NoAccessPage /> : <EnrolmentImportWizardPage />
  if (props.view.kind === 'courses-list')
    return (
      <CoursesListPage
        onNew={() => props.navigateTo({ kind: 'course-new' }, '/courses/new')}
        onImport={() => props.navigateTo({ kind: 'enrolment-import' }, '/courses/import')}
        onOpen={(course) =>
          props.navigateTo({ kind: 'course-detail', courseId: course.id }, `/courses/${course.id}`)
        }
        onEdit={(course) =>
          props.navigateTo(
            { kind: 'course-edit', courseId: course.id },
            `/courses/${course.id}/edit`,
          )
        }
      />
    )
  if (props.view.kind === 'course-detail') {
    const courseId = props.view.courseId
    return (
      <CourseDetailPage
        courseId={courseId}
        onBack={() => props.navigateTo({ kind: 'courses-list' }, '/courses')}
        onEdit={() =>
          props.navigateTo({ kind: 'course-edit', courseId }, `/courses/${courseId}/edit`)
        }
      />
    )
  }
  if (props.view.kind === 'course-new' || props.view.kind === 'course-edit') {
    const courseId = props.view.kind === 'course-edit' ? props.view.courseId : undefined
    return (
      <CourseFormPage
        key={courseId ?? 'new'}
        courseId={courseId}
        onSaved={(id) =>
          props.navigateTo({ kind: 'course-detail', courseId: id }, `/courses/${id}`)
        }
        onCancel={() =>
          courseId
            ? props.navigateTo({ kind: 'course-detail', courseId }, `/courses/${courseId}`)
            : props.navigateTo({ kind: 'courses-list' }, '/courses')
        }
      />
    )
  }
  if (isEventsView(props.view))
    return (
      <EventsPage
        view={props.view}
        navigateTo={props.navigateTo}
        canCreateSegments={
          canManage(props.capabilities, 'membership') ||
          canManage(props.capabilities, 'communications')
        }
      />
    )
  if (props.view.kind === 'event-registrations')
    return (
      <EventRegistrationsPage
        onOpenEvent={(eventId) =>
          props.navigateTo({ kind: 'event-detail', eventId }, `/events/manage/${eventId}`)
        }
      />
    )
  return <StaffNonEventContent {...props} />
}

interface StaffPageContentProps {
  view: ContactsView
  setView: React.Dispatch<React.SetStateAction<ContactsView>>
  navigateTo: (view: ContactsView, path: string) => void
  association: Association
  message: string | null
  showList: () => void
  showMembers: () => void
  showInvoices: () => void
  openInvoice: (invoice: Invoice) => void
  openInvoiceById: (invoiceId: number) => void
  openIncomeDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
  openAgingDrillDown: (drillDown: AgingContactDrillDown) => void
  onOpenCampaign: (campaignId: number) => void
  editContact: (contactId: number, contactType: Contact['contact_type']) => void
  profileReturn: { label: string; onReturn: () => void }
  viewOnly: boolean
  viewerEmail: string
  capabilities: StaffIdentity['user']['seats'][number]['capabilities']
}

// eslint-disable-next-line complexity
function StaffNonEventContent({
  view,
  setView,
  navigateTo,
  association,
  message,
  showList,
  showInvoices,
  openInvoice,
  openInvoiceById,
  openIncomeDrillDown,
  openAgingDrillDown,
  onOpenCampaign,
  editContact,
  profileReturn,
  viewOnly,
  viewerEmail,
  capabilities,
}: StaffPageContentProps) {
  const openOrganization = (contactId: number) =>
    navigateTo({ kind: 'organization-profile', contactId }, `/contacts/organizations/${contactId}`)
  return view.kind === 'list' ? (
    <ContactsListPage
      message={message}
      onCreateContact={() => setView({ kind: 'new' })}
      onOpenPerson={(contact) =>
        setView({ kind: 'person-profile', contactId: contact.id, origin: 'contacts' })
      }
      onOpenOrganization={(contact) => openOrganization(contact.id)}
      onEditContact={(contact) => editContact(contact.id, contact.contact_type)}
    />
  ) : view.kind === 'chapters' || view.kind === 'chapter-detail' ? (
    <StaffChapterContent view={view} navigateTo={navigateTo} />
  ) : isMembershipView(view) ? (
    <StaffMembershipContent
      view={view}
      currentAssociationDate={association.current_date}
      onOpenMember={(contactId) =>
        setView({ kind: 'person-profile', contactId, origin: 'members' })
      }
      onOpenOrganization={openOrganization}
    />
  ) : isCommunicationView(view) ? (
    <StaffCommunicationView
      view={view}
      navigateTo={navigateTo}
      segmentManage={canManage(capabilities, 'segments')}
      groupManage={canManage(capabilities, 'groups')}
    />
  ) : isSettingsView(view) ? (
    <StaffSettingsView
      view={view}
      navigateTo={navigateTo}
      currentAssociationDate={association.current_date}
      onOpenMember={(contactId) =>
        setView({ kind: 'person-profile', contactId, origin: 'members' })
      }
      viewOnly={viewOnly}
      viewerEmail={viewerEmail}
      capabilities={capabilities}
    />
  ) : isFinanceView(view) ? (
    <StaffFinanceView
      view={view}
      currentAssociationDate={association.current_date}
      onOpenInvoice={openInvoice}
      onOpenInvoiceById={openInvoiceById}
      onBackToInvoices={showInvoices}
      onOpenMember={(contactId) =>
        setView({ kind: 'person-profile', contactId, origin: 'members' })
      }
      onIncomeDrillDown={openIncomeDrillDown}
      onAgingDrillDown={openAgingDrillDown}
      onOpenCampaign={onOpenCampaign}
    />
  ) : viewOnly && (view.kind === 'duplicates' || view.kind === 'import') ? (
    <NoAccessPage />
  ) : view.kind === 'duplicates' ? (
    <DuplicateReviewPage />
  ) : view.kind === 'import' ? (
    <ContactImportWizardPage />
  ) : view.kind === 'person-profile' ? (
    <PersonProfilePage
      contactId={view.contactId}
      currentAssociationDate={association.current_date}
      currentAssociationTimeZone={association.time_zone}
      backLabel={profileReturn.label}
      onBack={profileReturn.onReturn}
      onEdit={() => editContact(view.contactId, 'Person')}
      onLifecycleComplete={profileReturn.onReturn}
      onOpenInvoice={openInvoice}
      capabilities={capabilities}
    />
  ) : view.kind === 'organization-dashboard-preview' ? (
    <OrganizationDashboardPreview
      key={`${association.id}:${viewerEmail}:${view.contactId}`}
      contactId={view.contactId}
      viewer={JSON.stringify([association.id, viewerEmail, capabilities])}
    />
  ) : view.kind === 'organization-profile' ? (
    <OrganizationProfilePage
      contactId={view.contactId}
      currentAssociationDate={association.current_date}
      onBack={showList}
      onEdit={() => editContact(view.contactId, 'Organization')}
      onLifecycleComplete={showList}
      onOpenInvoice={openInvoice}
      capabilities={capabilities}
    />
  ) : (
    <ContactFormPage
      key={view.kind === 'edit' ? `edit-${view.contactType}-${view.contactId}` : 'new'}
      contactId={view.kind === 'edit' ? view.contactId : undefined}
      contactType={view.kind === 'edit' ? view.contactType : undefined}
      onCancel={showList}
      onSaved={showList}
    />
  )
}

function StaffChapterContent({
  view,
  navigateTo,
}: Pick<StaffPageContentProps, 'view' | 'navigateTo'>) {
  if (view.kind === 'chapter-detail') {
    return (
      <ChapterRecordPage
        chapterId={view.chapterId}
        onBack={() => navigateTo({ kind: 'chapters' }, '/contacts/chapters')}
      />
    )
  }
  return (
    <ChaptersListPage
      onOpenChapter={(chapter) =>
        navigateTo(
          { kind: 'chapter-detail', chapterId: chapter.id },
          `/contacts/chapters/${chapter.id}`,
        )
      }
    />
  )
}

function personProfileReturn(view: ContactsView, showMembers: () => void, showList: () => void) {
  if (view.kind === 'person-profile' && view.origin === 'members') {
    return { label: 'Members', onReturn: showMembers }
  }

  return { label: 'Contacts', onReturn: showList }
}
